import {AnimatePresence, m} from 'framer-motion'
import _ from 'lodash'
import {FC, useEffect, useRef, useState} from 'react'

const DOT_COUNT = 3
const DOT_SIZE = 6
const DOT_GAP = 8
const DOT_UNIT = DOT_SIZE + DOT_GAP
const DOT_OFFSET = DOT_UNIT / 2
const MOVE_MS = 150
const CYCLE_PAUSE_MS = 200

// Absolute x positions for each dot slot; derived so adding dots only requires changing DOT_COUNT
const POSITIONS = Array.from({length: DOT_COUNT}, (_, i) => i * DOT_UNIT + DOT_OFFSET)
// Exit target is one stride past the last slot; button overflow:hidden clips it
const POS_EXIT = DOT_COUNT * DOT_UNIT + DOT_OFFSET

// isNew distinguishes dots entering mid-animation (slide-in) from those placed on mount
type LoadingDot = {id: number; x: number; isNew: boolean}

export const LoadingDots: FC<{color: string}> = ({color}) => {
  const idRef = useRef(0)
  const [dots, setDots] = useState<LoadingDot[]>(() =>
    POSITIONS.map((x) => ({id: idRef.current++, x, isNew: false})),
  )

  useEffect(() => {
    let mounted = true
    let timer: ReturnType<typeof setTimeout>

    // Runs steps sequentially with MOVE_MS between each.
    // Only one timer is active at a time, so clearTimeout(timer) in cleanup is always sufficient.
    const runChain = (steps: Array<() => void>) => {
      if (steps.length === 0) return
      timer = setTimeout(() => {
        if (!mounted) return
        steps[0]()
        runChain(steps.slice(1))
      }, MOVE_MS)
    }

    const cycle = () => {
      timer = setTimeout(() => {
        if (!mounted) return

        // Last dot exits
        setDots((prev) => prev.filter((d) => d.x !== POSITIONS[DOT_COUNT - 1]))

        // Shift each dot one step right, from back to front
        const shifts = Array.from({length: DOT_COUNT - 1}, (_, i) => {
          const fromIdx = DOT_COUNT - 2 - i
          return () =>
            setDots((prev) =>
              prev.map((d) => (d.x === POSITIONS[fromIdx] ? {...d, x: POSITIONS[fromIdx + 1]} : d)),
            )
        })

        // New dot enters at front, then schedule next cycle
        const enter = () => {
          setDots((prev) => [{id: idRef.current++, x: POSITIONS[0], isNew: true}, ...prev])
          timer = setTimeout(cycle, MOVE_MS + CYCLE_PAUSE_MS)
        }

        runChain([...shifts, enter])
      }, CYCLE_PAUSE_MS)
    }

    cycle()
    return () => {
      mounted = false
      clearTimeout(timer)
    }
  }, [])

  return (
    <>
      <div className="loading-dots">
        <AnimatePresence>
          {dots.map((dot) => (
            <m.span
              key={dot.id}
              // New dots slide in from just left of position 0; mount dots spread from the left edge
              initial={{x: dot.isNew ? -DOT_UNIT / 2 : 0}}
              animate={{x: dot.x, opacity: 1}}
              transition={{
                x: {duration: MOVE_MS / 1000, ease: 'easeInOut'},
                opacity: {duration: 0.25, ease: 'easeOut'},
              }}
              exit={{
                x: POS_EXIT,
                opacity: 0,
                transition: {
                  x: {duration: MOVE_MS / 1000, ease: 'easeInOut'},
                  opacity: {duration: 0.25, ease: 'easeOut'},
                },
              }}
              style={{
                display: 'block',
                position: 'absolute',
                top: 0,
                width: DOT_SIZE,
                height: DOT_SIZE,
                borderRadius: '50%',
                backgroundColor: color,
              }}
              onUpdate={_.noop}
            />
          ))}
        </AnimatePresence>
      </div>

      <style jsx>{`
        .loading-dots {
          width: ${DOT_COUNT * DOT_UNIT + DOT_SIZE}px;
          height: ${DOT_SIZE}px;
          position: relative;
        }
      `}</style>
    </>
  )
}
